/**
 * LỊCH SỬ CHẤM CÔNG.
 *
 * One row per `attendance_events` record, showing what the server recorded and
 * — crucially — how it judged the record. An employee who can see that last
 * Tuesday's check-in was flagged OUT_OF_GEOFENCE can appeal it while they still
 * remember why; the same fact discovered on payday is an argument.
 *
 * `serverTime` is shown as THE time. `deviceTime` and `clockSkewMs` are shown
 * beside it only when they disagree, so the employee understands why a record
 * was questioned.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAttendanceHistory, errorMessage } from '../lib/queries';
import { ATTENDANCE_ACTION_LABEL, RISK_FLAG_ADVICE, RISK_FLAG_LABEL, verificationLabel, verificationTone } from '../lib/labels';
import type { AttendanceActionType, AttendanceHistoryEvent, AttendanceRiskFlag } from '../lib/contracts';
import { formatBusinessDate, formatDistance, formatSkew, formatTime, toDateInput } from '../lib/format';
import { Button, Card, EmptyState, ErrorState, LoadingState, StatusBadge } from '../components/ui';
import { ScreenHeader } from '../components/ScreenHeader';

/** The API filters on `business_date` with `gte(from)` / `lt(to)`. */
function defaultRange(): { from: string; to: string } {
  const today = new Date();
  const from = new Date(today);
  from.setDate(from.getDate() - 30);
  const to = new Date(today);
  // `lt` is exclusive, so "to" must be tomorrow for today to be included.
  to.setDate(to.getDate() + 1);
  return { from: toDateInput(from), to: toDateInput(to) };
}

function isRiskFlag(value: unknown): value is AttendanceRiskFlag {
  return typeof value === 'string' && value in RISK_FLAG_LABEL;
}

function isAction(value: string): value is AttendanceActionType {
  return value in ATTENDANCE_ACTION_LABEL;
}

export function AttendanceHistoryScreen(): JSX.Element {
  const navigate = useNavigate();
  const [range, setRange] = useState(defaultRange);
  const { data, isLoading, isError, error, refetch } = useAttendanceHistory(range.from, range.to);

  return (
    <div className="pb-8">
      <ScreenHeader title="Lịch sử chấm công" subtitle="30 ngày gần nhất theo mặc định" />

      <Card className="mb-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="from">
              Từ ngày
            </label>
            <input
              id="from"
              type="date"
              className="field mt-1"
              value={range.from}
              onChange={(event) => setRange((prev) => ({ ...prev, from: event.target.value }))}
            />
          </div>
          <div>
            <label className="label" htmlFor="to">
              Đến ngày
            </label>
            <input
              id="to"
              type="date"
              className="field mt-1"
              value={range.to}
              onChange={(event) => setRange((prev) => ({ ...prev, to: event.target.value }))}
            />
          </div>
        </div>
      </Card>

      {isLoading ? <LoadingState /> : null}
      {isError ? <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} /> : null}

      {data === undefined || isLoading ? null : data.events.length === 0 ? (
        <EmptyState title="Không có lần chấm công nào trong khoảng này" />
      ) : (
        <ul className="space-y-3">
          {data.events.map((event) => (
            <li key={event.id}>
              <EventCard event={event} onAppeal={() => navigate('/ca-nhan/khieu-nai', { state: { attendanceEventId: event.id } })} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function EventCard({
  event,
  onAppeal,
}: {
  readonly event: AttendanceHistoryEvent;
  readonly onAppeal: () => void;
}): JSX.Element {
  const flags = event.riskFlags.filter(isRiskFlag);
  const questionable = event.verificationResult !== 'PASSED';

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-bold text-slate-900">
            {isAction(event.actionType) ? ATTENDANCE_ACTION_LABEL[event.actionType] : event.actionType}
          </p>
          <p className="text-sm text-slate-500">{formatBusinessDate(event.businessDate)}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-black tabular-nums text-slate-900">{formatTime(event.serverTime)}</p>
          <p className="text-xs text-slate-400">giờ máy chủ</p>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap items-center gap-2">
        <StatusBadge tone={verificationTone(event.verificationResult)}>
          {verificationLabel(event.verificationResult)}
        </StatusBadge>
        <span className="text-sm text-slate-500">
          {formatDistance(event.distanceFromBranchM)} · {event.verificationMethod}
        </span>
      </div>

      {event.clockSkewMs !== null && Math.abs(event.clockSkewMs) > 60_000 ? (
        <p className="mt-2 text-sm text-amber-800">
          Giờ điện thoại lúc đó {formatSkew(event.clockSkewMs)}. Con số dùng để tính công vẫn là giờ máy chủ ở
          trên.
        </p>
      ) : null}

      {flags.length === 0 ? null : (
        <ul className="mt-3 space-y-2">
          {flags.map((flag) => (
            <li key={flag} className="rounded-xl bg-slate-50 p-3">
              <p className="text-sm font-semibold text-slate-800">{RISK_FLAG_LABEL[flag]}</p>
              <p className="mt-0.5 text-sm text-slate-600">{RISK_FLAG_ADVICE[flag]}</p>
            </li>
          ))}
        </ul>
      )}

      {questionable ? (
        <Button variant="secondary" size="lg" fullWidth className="mt-3" onClick={onAppeal}>
          Khiếu nại lần chấm công này
        </Button>
      ) : null}
    </Card>
  );
}
